import { createSelector } from 'reselect';
import {
  extend,
  flow,
  fromPairs,
  get,
  map,
  snakeCase,
  toPairs,
  toUpper,
  union,
} from 'lodash/fp';
import { createDuck } from 'helpers/duckTools';
import api from 'store/actions/api';
import prepareOptions, {
  prepareSortedOptions,
} from 'helpers/fieldOptions/prepareOptions';
import {
  MAKE_OTHER_KEY,
  MAKE_OTHER_VALUE,
} from 'helpers/fieldOptions/car/make';

export const manufacturersDuck = createDuck(
  'MANUFACTURERS',
  {
    result: {},
    error: null,
    isLoading: false,
    isLoaded: false,
  },
  {
    loaders: {
      loadManufacturers: {
        fetch: api.getCarManufacturers,
      },
    },
  },
);

// Loaded during App init on every page — a rejection here 500s the whole SSR
// render, so degrade to an empty make filter instead.
manufacturersDuck.tryLoad = function tryLoad(...args) {
  return function* $tryLoad() {
    try {
      yield manufacturersDuck.loadManufacturers(...args);
    } catch (error) {
      console.error(error);
    }
  };
};

const MAKE_OTHER = {
  label: __('search.vehicleOptions.other'),
  value: MAKE_OTHER_VALUE,
};

const getManufacturersWkda = get(['manufacturers', 'result', 'wkda']);

export const selectManufacturerOptions = createSelector(
  getManufacturersWkda,
  (wkda) => {
    if (!wkda) return [];

    return flow(
      toPairs,
      map(([value, label]) => ({ label, value })),
      prepareSortedOptions,
      union(prepareOptions([MAKE_OTHER])),
    )(wkda);
  },
);

// `OTHER` is synthetic — the API never returns it, but the dropdown offers it,
// so both directions have to carry it or picking it drops `brand` from the URL
// and the make is lost on the way back.
// Exported as plain functions for the server-side meta-to-query, which has no
// store.
export const toCarMakeValues = flow(
  toPairs,
  map(([code, name]) => [toUpper(snakeCase(name)), code]),
  fromPairs,
  extend({ [MAKE_OTHER_KEY]: MAKE_OTHER_VALUE }),
);

export const toCarMakeById = flow(
  toPairs,
  map(([code, name]) => [code, toUpper(snakeCase(name))]),
  fromPairs,
  extend({ [MAKE_OTHER_VALUE]: MAKE_OTHER_KEY }),
);

export const selectCarMakeValues = createSelector(
  getManufacturersWkda,
  (wkda) => {
    if (!wkda) return {};

    return toCarMakeValues(wkda);
  },
);

export const selectCarMakeById = createSelector(
  getManufacturersWkda,
  (wkda) => {
    if (!wkda) return {};

    return toCarMakeById(wkda);
  },
);
